import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/hooks/useCartSync';
import { useAuth } from '@/hooks/useAuth';
import { useCurrency } from '@/hooks/useCurrency';
import { formatPriceWithCurrency } from '@/utils/currency';
import { supabase } from '@/integrations/supabase/client';
import PaymentMethod from '@/components/PaymentMethod';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Minus, Plus, Trash2, MapPin, ShoppingBag } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/hooks/useLanguage';

interface Relay {
  id: string;
  name: string;
  address: string;
  active: boolean;
}

const Cart = () => {
  const { items, removeItem, updateQuantity, clearCart, getTotalPrice, isOpen, setIsOpen } = useCart();
  const { user } = useAuth();
  const { displayCurrency, convertPrice } = useCurrency();
  const { toast } = useToast();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [relays, setRelays] = useState<Relay[]>([]);
  const [selectedRelay, setSelectedRelay] = useState<string>('');
  const [loadingRelays, setLoadingRelays] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [directPurchaseMode, setDirectPurchaseMode] = useState(false);

  // Load relays when cart opens
  const loadRelays = async () => {
    setLoadingRelays(true);
    try {
      const { data, error } = await supabase
        .from('relays')
        .select('id, name, address, active')
        .eq('active', true)
        .order('name', { ascending: true });
      
      if (error) {
        console.error('Error loading relays:', error);
        toast({
          title: t('cart.error'),
          description: t('cart.errorLoadingRelays'),
          variant: "destructive"
        });
        return;
      }

      setRelays(data || []);
      
      // Auto-select first relay if none selected
      if (data && data.length > 0 && !selectedRelay) {
        setSelectedRelay(data[0].id);
      }
    } catch (error) {
      console.error('Error loading relays:', error);
      toast({
        title: t('cart.error'),
        description: t('cart.errorLoadingRelays'),
        variant: "destructive"
      });
    } finally {
      setLoadingRelays(false);
    }
  };

  // Handle opening cart
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      loadRelays();
      // Check for direct purchase mode
      const directPurchase = sessionStorage.getItem('directPurchase');
      if (directPurchase) {
        setDirectPurchaseMode(true);
      } else {
        setDirectPurchaseMode(false);
      }
    }
  };

  // Ensure relays are fetched and check for direct purchase when the cart becomes visible
  useEffect(() => {
    if (isOpen) {
      loadRelays();
      // Check for direct purchase mode
      const directPurchase = sessionStorage.getItem('directPurchase');
      if (directPurchase) {
        setDirectPurchaseMode(true);
      } else {
        setDirectPurchaseMode(false);
      }
    }
  }, [isOpen]);

  // Listen for payment success messages from other tabs
  useEffect(() => {
    // Listen via BroadcastChannel
    const paymentChannel = new BroadcastChannel('payment-channel');
    paymentChannel.onmessage = (event) => {
      if (event.data?.type === 'PAYMENT_SUCCESS') {
        clearCart();
        setShowPayment(false);
        toast({
          title: t('cart.paymentConfirmed'),
          description: t('cart.cartCleared'),
        });
      }
    };

    // Also listen via postMessage
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (data?.type === 'PAYMENT_SUCCESS') {
        clearCart();
        setShowPayment(false);
        toast({
          title: t('cart.paymentConfirmed'),
          description: t('cart.cartCleared'),
        });
      }
    };

    window.addEventListener('message', onMessage);

    return () => {
      paymentChannel.close();
      window.removeEventListener('message', onMessage);
    };
  }, [clearCart, toast]);

  const formatPrice = (cents: number, currency: string) => {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  };

  // Get items to display (either direct purchase or cart items)
  const displayItems = (() => {
    if (directPurchaseMode) {
      const directPurchaseData = sessionStorage.getItem('directPurchase');
      if (directPurchaseData) {
        const { item } = JSON.parse(directPurchaseData);
        return [{
          id: item.product_id,
          title: `${item.title}${item.color ? ` - ${item.color}` : ''}${item.size ? ` - Taille ${item.size}` : ''}`,
          price_cents: item.price_cents,
          currency: item.currency,
          quantity: item.quantity,
          images: item.images,
          vendor_id: item.vendor_id
        }];
      }
    }
    return items;
  })();

  // Calcule le total directement dans la devise affichée, ligne par ligne
  // Utilise displayItems pour prendre en compte l'achat direct
  const convertedTotalPrice = displayItems.reduce((sum, item) => {
    const itemTotal = item.price_cents * item.quantity;
    const normalizeFrom = (c: string) => (c === 'FC' ? 'CDF' : c);
    const from = normalizeFrom(item.currency);
    const to = displayCurrency;
    return sum + (from === to ? itemTotal : convertPrice(itemTotal, from, to));
  }, 0);

  // Handle clear cart - also clears direct purchase data
  const handleClearCart = () => {
    clearCart();
    sessionStorage.removeItem('directPurchase');
    setDirectPurchaseMode(false);
  };


  const handleCheckout = async () => {
    if (!user) {
      toast({
        title: t('cart.loginRequired'),
        description: t('cart.loginRequiredDesc'),
        variant: "destructive"
      });
      return;
    }

    if (!selectedRelay) {
      toast({
        title: t('cart.relayRequired'),
        description: t('cart.relayRequiredDesc'),
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      // Check if in direct purchase mode
      const directPurchaseData = sessionStorage.getItem('directPurchase');
      
      if (directPurchaseData) {
        // Direct purchase mode: use the single item from sessionStorage
        const { item } = JSON.parse(directPurchaseData);

        const pendingPayload = {
          items: [item],
          relayId: selectedRelay,
          clientId: user.id,
          createdAt: Date.now()
        };

        sessionStorage.setItem('pendingCart', JSON.stringify(pendingPayload));
        localStorage.setItem('pendingCartBackup', JSON.stringify(pendingPayload));
        
      } else {
        // Normal cart mode: process all items
        const cartItemsWithCommission = await Promise.all(
          items.map(async (item) => {
            // Fetch product category to get commission rate
            const { data: product } = await supabase
              .from('products')
              .select(`
                id,
                category_id,
                categories:category_id (
                  commission_rate
                )
              `)
              .eq('id', item.id)
              .single();

            const commissionRate = product?.categories?.commission_rate ?? 0.15;
            const subtotal = item.price_cents * item.quantity;
            const commissionAmount = Math.round(subtotal * commissionRate);
            const netAmount = subtotal - commissionAmount;

            return {
              product_id: item.id,
              vendor_id: item.vendor_id,
              quantity: item.quantity,
              price_cents: item.price_cents,
              currency: item.currency,
              subtotal_cents: subtotal,
              commission_rate: commissionRate,
              commission_cents: commissionAmount,
              net_cents: netAmount
            };
          })
        );
        
        // Store cart data for payment processing.
        // localStorage backup is needed when payment callback opens in a new tab.
        const pendingPayload = {
          items: cartItemsWithCommission,
          relayId: selectedRelay,
          clientId: user.id,
          createdAt: Date.now()
        };

        sessionStorage.setItem('pendingCart', JSON.stringify(pendingPayload));
        localStorage.setItem('pendingCartBackup', JSON.stringify(pendingPayload));
      }
      
      setShowPayment(true);

      toast({
        title: t('cart.orderPrepared'),
        description: t('cart.choosePayment')
      });

    } catch (error: any) {
      console.error('[Cart] Error preparing checkout:', error);
      toast({
        title: t('cart.error'),
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    clearCart();
    sessionStorage.removeItem('pendingCart');
    localStorage.removeItem('pendingCartBackup');
    sessionStorage.removeItem('directPurchase'); // Clean up direct purchase data
    setIsOpen(false);
    setShowPayment(false);
    setDirectPurchaseMode(false);
    
    toast({
      title: t('cart.paymentSuccess'),
      description: t('cart.orderConfirmed')
    });
    
    // Redirect to dashboard
    navigate('/dashboard');
  };

  const handlePaymentCancel = () => {
    setShowPayment(false);
    // Cart items remain in cart, no orders created
  };

  const handleContinueShopping = () => {
    setIsOpen(false);
    sessionStorage.removeItem('directPurchase'); // Clean up if user cancels
    setDirectPurchaseMode(false);
    navigate('/products');
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col overflow-hidden">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="flex items-center space-x-2">
            <ShoppingBag className="h-5 w-5" />
            <span>{directPurchaseMode ? 'Achat direct' : t('cart.title')}</span>
            {displayItems.length > 0 && (
              <Badge variant="secondary">{displayItems.length}</Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            {displayItems.length === 0 
              ? t('cart.empty')
              : directPurchaseMode 
                ? 'Prêt pour le paiement'
                : `${displayItems.length} ${t('cart.productsInCart')}`
            }
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col min-h-0 pb-28 sm:pb-32">
          {showPayment ? (
            <div
              className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-2"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <PaymentMethod
                totalAmount={convertedTotalPrice}
                onPaymentSuccess={handlePaymentSuccess}
                onCancel={handlePaymentCancel}
              />
            </div>
          ) : displayItems.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingBag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t('cart.empty')}</p>
                <Button 
                  className="mt-4" 
                  onClick={handleContinueShopping}
                >
                  {t('cart.continueShopping')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto py-4 space-y-4">
                {displayItems.map((item) => (
                  <Card key={item.id}>
                    <CardContent className="p-4">
                      <div className="flex space-x-4">
                        {item.images?.[0] && (
                          <img
                            src={item.images[0]}
                            alt={item.title}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm truncate">{item.title}</h4>
                          <p className="text-xs text-muted-foreground">
                            {t('cart.by')} {item.vendor_name || t('cart.vendor')}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {formatPriceWithCurrency(item.price_cents, item.currency, displayCurrency, convertPrice)} 
                            <span className="text-xs ml-1">({item.currency})</span>
                          </p>
                          
                          {/* Quantity Controls - Hide in direct purchase mode */}
                          {!directPurchaseMode && (
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center space-x-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                  disabled={item.quantity <= 1}
                                >
                                  <Minus className="h-3 w-3" />
                                </Button>
                                <span className="w-8 text-center text-sm">{item.quantity}</span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                              </div>
                              
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => removeItem(item.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                          
                          {/* Quantity display in direct purchase mode */}
                          {directPurchaseMode && (
                            <div className="mt-2">
                              <p className="text-sm text-muted-foreground">
                                {t('cart.quantity')}: {item.quantity}
                              </p>
                            </div>
                          )}
                          
                          {/* Subtotal */}
                          <p className="text-sm font-semibold mt-2">
                            {t('cart.total')}: {formatPriceWithCurrency(item.price_cents * item.quantity, item.currency, displayCurrency, convertPrice)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Relay Selection */}
              <div className="space-y-4 border-t pt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center space-x-2">
                    <MapPin className="h-4 w-4" />
                    <span>{t('cart.pickupPoint')}</span>
                  </label>
                  <Select value={selectedRelay} onValueChange={setSelectedRelay}>
                    <SelectTrigger className="w-full bg-background border-input hover:border-primary focus:border-primary">
                      <SelectValue placeholder={loadingRelays ? t('cart.loading') : t('cart.chooseRelay')} />
                    </SelectTrigger>
                     <SelectContent className="bg-background border border-border shadow-elegant z-[9999] max-h-60 overflow-y-auto rounded-lg" position="popper" sideOffset={8}>
                       {loadingRelays ? (
                         <SelectItem value="loading" disabled>
                           {t('cart.loadingRelays')}
                         </SelectItem>
                       ) : relays.length === 0 ? (
                         <SelectItem value="empty" disabled>
                           {t('cart.noRelays')}
                         </SelectItem>
                       ) : (
                         relays.map((relay) => (
                           <SelectItem 
                             key={relay.id} 
                             value={relay.id} 
                             className="cursor-pointer hover:bg-accent-hover focus:bg-accent data-[highlighted]:bg-accent transition-smooth rounded-md mx-1 my-0.5"
                           >
                             <div className="flex flex-col w-full py-2 px-1">
                               <p className="font-medium text-foreground text-sm">{relay.name}</p>
                               <p className="text-xs text-muted-foreground mt-0.5">{relay.address}</p>
                             </div>
                           </SelectItem>
                         ))
                       )}
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                {/* Total */}
                <div className="space-y-2">
                  <div className="flex justify-between text-lg font-bold">
                    <span>{t('cart.total')}</span>
                    <span>
                      {formatPrice(convertedTotalPrice, displayCurrency)}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <Button
                    className="w-full bg-gradient-primary"
                    onClick={handleCheckout}
                    disabled={loading || !selectedRelay}
                  >
                    {loading ? t('cart.processing') : t('cart.placeOrder')}
                  </Button>
                  
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={handleContinueShopping}
                    >
                      {t('cart.continue')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleClearCart}
                    >
                      {t('cart.clear')}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default Cart;